import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Box, GlobalStyles, Typography } from '@mui/material';

import DataGrid from '~/components/General/OlcDataGrid';
import useListUrlQuery from '~/hooks/useListUrlQuery';
import useSortParams from '~/hooks/useSortParams';
import NoRecords from '~/components/General/NoRecords';
import OlcPageBackButton from '~/components/General/OlcPageBackButton';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import TruncatedText from '~/components/General/TruncatedText';

import { fetchQrCodeScannedContacts } from '~/redux/actions/qr-codes-actions';

import { dateFormat } from '~/utils/date-format';
import { ENGAGEMENT_HISTORY_SORT_KEYS } from '~/utils/listQueryParams';
import { DEFAULT_PAGINATION, ENGAGEMENT_TYPES } from '~/utils/constants';

import { CustomFieldIcon } from '~/assets/images';

const getHistoryContact = (row) => row?.contact || row?.contactData || {};

// A history row links to its contact; a deleted contact has no id left on the row (nothing to open).
const getHistoryContactId = (row) => row?.contactId ?? row?.contactData?.id ?? null;

const PurlHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const scannedContacts = useSelector((state) => state.qrCodesReducers.scannedContacts);
  const totalCount = useSelector((state) => state.qrCodesReducers.scannedContactsRowsCount);
  const loading = useSelector((state) => state.qrCodesReducers.scannedContactsLoader);
  const from = useSelector((state) => state.qrCodesReducers.scannedContactsFrom);
  const to = useSelector((state) => state.qrCodesReducers.scannedContactsTo);
  const lastPage = useSelector((state) => state.qrCodesReducers.scannedContactsLastPage);
  const currentPage = useSelector((state) => state.qrCodesReducers.scannedContactsCurrentPage);
  const perPage = useSelector((state) => state.qrCodesReducers.scannedContactsPerPage);

  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION, pageSize: 20 });
  const [loadedRequestKey, setLoadedRequestKey] = useState(null);
  // The column sort lives in the URL (the source of truth), so it survives reload and shared links.
  const { sort, updateUrl: updateHistoryUrl } = useListUrlQuery(ENGAGEMENT_HISTORY_SORT_KEYS);

  const purlId = params.id;
  const sortKey = `${sort.sortBy ?? ''}:${sort.sortOrder ?? ''}`;
  const requestKey = `${purlId}:${pagination.page}:${pagination.pageSize}:${sortKey}`;

  useEffect(() => {
    let isActive = true;

    const loadHistory = async () => {
      if (!purlId) return;

      await dispatch(
        fetchQrCodeScannedContacts(purlId, {
          page: pagination.page,
          pageSize: pagination.pageSize,
          engagementType: ENGAGEMENT_TYPES.PURL,
          ...sort,
        })
      );

      if (isActive) {
        setLoadedRequestKey(requestKey);
      }
    };

    if (purlId) {
      loadHistory();
    }

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize, purlId, sort.sortBy, sort.sortOrder]);

  const showLoader = loadedRequestKey !== requestKey || loading;
  const pageSizes = [20, 50, 100];

  const handleChangePage = (_event, newPage) => {
    setPagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };

  const handleBackToPurls = () => {
    navigate({
      pathname: '/engagement/purls',
      search: location.state?.listQuery ? `?${location.state.listQuery}` : '',
    });
  };

  // Opens the contact (Ctrl/Cmd-click: new tab, as in the Contacts table).
  const handleRowClick = (rowParams, event) => {
    const contactId = getHistoryContactId(rowParams.row);

    if (!contactId) return;

    if (event.ctrlKey || event.metaKey) {
      window.open(`/contacts/${contactId}`, '_blank');
    } else {
      navigate(`/contacts/${contactId}`);
    }
  };

  const columns = [
    {
      field: 'fullName',
      headerName: 'Full Name',
      flex: 1,
      minWidth: 150,
      sortable: true,
      renderCell: (params) => (
        <TruncatedText
          text={`${getHistoryContact(params.row).firstName || ''} ${
            getHistoryContact(params.row).lastName || ''
          }`.trim()}
          maxLength={30}
        />
      ),
    },
    {
      field: 'companyName',
      headerName: 'Company Name',
      flex: 1,
      minWidth: 150,
      sortable: true,
      renderCell: (params) => (
        <TruncatedText text={getHistoryContact(params.row).companyName || ''} maxLength={30} />
      ),
    },
    {
      field: 'mailingAddress',
      headerName: 'Mailing Address',
      flex: 1.5,
      minWidth: 250,
      sortable: true,
      renderCell: (params) => (
        <TruncatedText
          htmlElement={
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {(getHistoryContact(params.row).address1 || '') +
                ' ' +
                (getHistoryContact(params.row).address2 || '')}
              <br />
              {(getHistoryContact(params.row).city || '') +
                ', ' +
                (getHistoryContact(params.row).state || '') +
                ' ' +
                (getHistoryContact(params.row).zip || '')}
            </Typography>
          }
          text={
            `${params?.row?.address1 || ''} ${params?.row?.address2 || ''}` +
            `${params?.row?.city || ''} ` +
            `${params?.row?.state || ''} ` +
            `${params?.row?.zip || ''} `
          }
          maxLength={30}
        />
      ),
    },
    {
      field: 'utmCampaignName',
      sortKey: 'campaignName',
      headerName: 'PURL Name',
      flex: 1,
      minWidth: 180,
      sortable: true,
      renderCell: (params) => (
        <TruncatedText
          text={
            params?.row?.purlName || params?.row?.campaignName || params?.row?.utmCampaignName || ''
          }
          maxLength={30}
        />
      ),
    },
    {
      field: 'totalScans',
      headerName: 'Total Visits',
      width: 120,
      sortable: true,
      renderCell: (params) => <Typography variant="body2">{params.row.totalScans || 0}</Typography>,
    },
    {
      field: 'lastScan',
      headerName: 'Last Scan',
      flex: 1,
      minWidth: 220,
      sortable: true,
      renderCell: (params) => (
        <Typography variant="body2">{dateFormat(params.row?.updatedAt) || '-'}</Typography>
      ),
    },
  ];

  // A new sort goes to the URL and the table returns to page 1; the fetch effect above reloads it.
  const { sortProps } = useSortParams(
    columns,
    (nextSort) => {
      setPagination((prev) => ({ ...prev, page: 1 }));
      updateHistoryUrl({ sort: nextSort });
    },
    sort
  );

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <Box className="olcWrapper olcSettingsListPage qrCodeHistoryPageWrapper">
        <Box className="olcScreenCard olcSettingsListCard qrCodeHistoryScreenCard">
          <Box className="qrHistoryHeader olcHeaderWrap">
            <OlcPageBackButton onClick={handleBackToPurls}>Back to PURLs</OlcPageBackButton>
          </Box>

          {!showLoader && scannedContacts.length === 0 ? (
            <Box>
              <NoRecords
                icon={
                  <img
                    src={CustomFieldIcon}
                    style={{ height: '33px', width: '33px' }}
                    alt="no-records"
                  />
                }
                title="No PURL History"
                message={
                  <>
                    <p style={{ margin: '0px' }}>No PURL visit history available yet.</p>
                    <p style={{ margin: '0px' }}>PURLs will appear here once they are visited.</p>
                  </>
                }
              />
            </Box>
          ) : (
            <Box
              className={`olcTableGridInternal ${showLoader ? 'table-loading' : ''} ${
                scannedContacts.length === 0 ? 'table-empty' : ''
              }`}
            >
              <Box sx={{ position: 'relative' }}>
                <DataGrid
                  rows={showLoader ? [] : scannedContacts}
                  columns={columns}
                  getRowId={(row) => row.id}
                  disableColumnMenu
                  disableRowSelectionOnClick
                  pagination={false}
                  {...sortProps}
                  rowHeight={61}
                  loading={showLoader}
                  onRowClick={handleRowClick}
                  getRowClassName={(rowParams) =>
                    getHistoryContactId(rowParams.row) ? 'clickableRow' : ''
                  }
                  sx={{
                    '& .MuiDataGrid-row.clickableRow:hover': {
                      cursor: 'pointer',
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                />
              </Box>

              {(showLoader || totalCount > 0) && (
                <OlcTablePagination
                  count={lastPage || 1}
                  page={currentPage || 1}
                  pageSize={perPage || pagination.pageSize}
                  pageSizes={pageSizes}
                  onPageChange={handleChangePage}
                  onPageSizeChange={(event) => {
                    setPagination({ page: 1, pageSize: event.target.value });
                  }}
                  from={from}
                  to={to}
                  total={totalCount}
                  loading={showLoader}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default PurlHistory;
